import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Trash, CaretLeft, CaretRight, X, CalendarBlank } from '@phosphor-icons/react';
import TravelModeBanner from '@/components/trips/TravelModeBanner';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const CATEGORIES = ['general', 'family', 'school', 'medical', 'maintenance', 'social', 'work'];
const CAT_COLORS = { general: '#4A5D4E', family: '#D97757', school: '#5C7361', medical: '#B85C3A', maintenance: '#E28B6D', social: '#7A7D78', work: '#3A6B4F' };

export default function CalendarPage() {
  const { api } = useAuth();
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', start_date: '', start_time: '', end_date: '', end_time: '', category: 'general', location: '', all_day: false, color: '#4A5D4E' });
  const [loading, setLoading] = useState(true);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;

  const fetchEvents = async () => {
    try {
      const res = await api.get(`/calendar/events?month=${monthStr}`);
      setEvents(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchEvents(); }, [api, monthStr]);

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date().toISOString().slice(0, 10);

  const changeMonth = (dir) => {
    setCurrentDate(new Date(year, month + dir, 1));
  };

  const getEventsForDate = (date) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(date).padStart(2, '0')}`;
    return events.filter(e => e.start_date <= dateStr && e.end_date >= dateStr);
  };

  const handleSaveEvent = async (e) => {
    e.preventDefault();
    try {
      await api.post('/calendar/events', { ...form, end_date: form.end_date || form.start_date });
      setShowForm(false);
      setForm({ title: '', description: '', start_date: '', start_time: '', end_date: '', end_time: '', category: 'general', location: '', all_day: false, color: '#4A5D4E' });
      fetchEvents();
    } catch (err) { console.error(err); }
  };

  const handleDeleteEvent = async (eventId) => {
    await api.delete(`/calendar/events/${eventId}`);
    fetchEvents();
  };

  const selectedDateStr = selectedDate ? `${year}-${String(month + 1).padStart(2, '0')}-${String(selectedDate).padStart(2, '0')}` : null;
  const selectedEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  return (
    <div data-testid="calendar-page" className="space-y-6">
      <TravelModeBanner context="calendar" />
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="overline">Family Calendar</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Schedule</h1>
        </div>
        <button onClick={() => { setShowForm(true); setForm({...form, start_date: selectedDateStr || today}); }} data-testid="add-event-btn"
          className="px-4 py-2 rounded-full bg-[#FF6B35] text-white btn-glow text-sm font-semibold flex items-center gap-2 self-start">
          <Plus size={18} /> Add Event
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar Grid */}
        <div className="lg:col-span-2 glass-card p-6">
          <div className="flex items-center justify-between mb-6">
            <button onClick={() => changeMonth(-1)} data-testid="prev-month-btn" className="p-2 rounded-lg hover:bg-muted"><CaretLeft size={20} /></button>
            <h2 className="font-heading text-xl font-bold">
              {currentDate.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
            </h2>
            <button onClick={() => changeMonth(1)} data-testid="next-month-btn" className="p-2 rounded-lg hover:bg-muted"><CaretRight size={20} /></button>
          </div>

          <div className="grid grid-cols-7 gap-1">
            {DAYS.map(d => (
              <div key={d} className="text-center py-2 text-xs font-medium text-muted-foreground">{d}</div>
            ))}
            {Array.from({ length: firstDay }).map((_, i) => <div key={`empty-${i}`} />)}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const dayEvents = getEventsForDate(day);
              const isToday = dateStr === today;
              const isSelected = selectedDate === day;

              return (
                <button
                  key={day}
                  onClick={() => setSelectedDate(day)}
                  data-testid={`calendar-day-${day}`}
                  className={`relative p-2 rounded-xl text-sm transition-colors min-h-[60px] md:min-h-[72px] flex flex-col items-start
                    ${isSelected ? 'bg-[#FF6B35] text-white btn-glow' : isToday ? 'bg-accent/10 text-accent' : 'hover:bg-muted'}`}
                >
                  <span className={`text-xs font-medium ${isToday && !isSelected ? 'text-accent font-bold' : ''}`}>{day}</span>
                  <div className="flex flex-wrap gap-0.5 mt-1">
                    {dayEvents.slice(0, 3).map((e, j) => (
                      <div key={j} className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-primary-foreground' : ''}`}
                        style={{ backgroundColor: isSelected ? undefined : (e.color || CAT_COLORS[e.category] || '#4A5D4E') }} />
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Day Detail */}
        <div className="glass-card p-6">
          <p className="overline mb-4">
            {selectedDate
              ? new Date(year, month, selectedDate).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' })
              : 'Select a day'}
          </p>
          {selectedDate ? (
            <div className="space-y-3">
              {selectedEvents.length > 0 ? selectedEvents.map(e => (
                <div key={e.event_id} className="rounded-xl border border-border p-4 card-hover" data-testid={`event-${e.event_id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="w-1 h-full min-h-[40px] rounded-full" style={{ backgroundColor: e.color || '#4A5D4E' }} />
                      <div>
                        <p className="font-medium text-sm">{e.title}</p>
                        {e.start_time && <p className="text-xs text-muted-foreground mt-0.5">{e.start_time}{e.end_time ? ` - ${e.end_time}` : ''}</p>}
                        {e.location && <p className="text-xs text-muted-foreground">{e.location}</p>}
                        {e.description && <p className="text-xs text-muted-foreground mt-1">{e.description}</p>}
                      </div>
                    </div>
                    <button onClick={() => handleDeleteEvent(e.event_id)} className="p-1 text-muted-foreground hover:text-destructive"><Trash size={14} /></button>
                  </div>
                </div>
              )) : (
                <p className="text-sm text-muted-foreground text-center py-8">No events on this day</p>
              )}
              <button onClick={() => { setShowForm(true); setForm({...form, start_date: selectedDateStr || today}); }}
                className="w-full py-2 rounded-xl border border-dashed border-border text-sm text-muted-foreground hover:bg-muted transition-colors flex items-center justify-center gap-2">
                <Plus size={16} /> Add event here
              </button>
            </div>
          ) : (
            <div className="text-center py-12">
              <CalendarBlank size={40} className="mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-sm text-muted-foreground">Click a date to see events</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Event Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowForm(false)} />
          <div className="relative bg-card border border-border rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-xl font-bold">Add Event</h3>
              <button onClick={() => setShowForm(false)} className="p-1 rounded-lg hover:bg-muted"><X size={20} /></button>
            </div>
            <form onSubmit={handleSaveEvent} className="space-y-4">
              <div>
                <label className="overline block mb-2">Title</label>
                <input type="text" value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="event-title-input" required
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50 focus:ring-1 focus:ring-[#FF6B35]/30" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline block mb-2">Start Date</label>
                  <input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} data-testid="event-start-date" required
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" />
                </div>
                <div>
                  <label className="overline block mb-2">Start Time</label>
                  <input type="time" value={form.start_time} onChange={e => setForm({...form, start_time: e.target.value})} data-testid="event-start-time"
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline block mb-2">End Date</label>
                  <input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} data-testid="event-end-date"
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" />
                </div>
                <div>
                  <label className="overline block mb-2">End Time</label>
                  <input type="time" value={form.end_time} onChange={e => setForm({...form, end_time: e.target.value})} data-testid="event-end-time"
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" />
                </div>
              </div>
              <div>
                <label className="overline block mb-2">Category</label>
                <select value={form.category} onChange={e => setForm({...form, category: e.target.value, color: CAT_COLORS[e.target.value] || '#4A5D4E'})} data-testid="event-category-select"
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none">
                  {CATEGORIES.map(c => <option key={c} value={c} className="capitalize">{c}</option>)}
                </select>
              </div>
              <div>
                <label className="overline block mb-2">Location</label>
                <input type="text" value={form.location} onChange={e => setForm({...form, location: e.target.value})} data-testid="event-location-input"
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" placeholder="Optional" />
              </div>
              <div>
                <label className="overline block mb-2">Description</label>
                <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="event-desc-input" rows={2}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none resize-none" />
              </div>
              <button type="submit" data-testid="save-event-btn" className="w-full py-3 rounded-full bg-[#FF6B35] text-white btn-glow font-semibold text-sm">Add Event</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
